import { useState, useMemo, useRef, useEffect } from "react";
import { LuSearch } from "react-icons/lu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tooltip } from "@/components/ui/tooltip";
import { RiArrowDownSLine, RiInformationLine } from "@remixicon/react";
import { Checkbox } from "@/components/ui/checkbox";
import { SqButton } from "@/components/squircle-ui/button";
import { searchSort } from "@/utils/searchSort";

export type SelectFilterOption<T> = {
  key: string;
  label: string;
  value: T;
  icon?: React.ComponentType<{ className?: string }> | null;
  info?: string;
};

export type SelectDropdownProps<T> = {
  options: SelectFilterOption<T>[];
  selected: T;
  defaultSelected?: T;
  onChange: (value: T) => void;
  onSetDefault?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  label?: string;
  /** Optional icon component to display next to the label */
  labelIcon?: React.ComponentType<{ className?: string }> | null;
  /** Position of the label relative to the selected option. Defaults to 'left' */
  labelPosition?: "left" | "right";
  placeholder?: string;
  /** Enable search functionality */
  enableSearch?: boolean;
  /** Placeholder text for search input */
  searchPlaceholder?: string;
  /** Show trigger outline/border. Defaults to true */
  showOutline?: boolean;
  /** Custom trigger className to override default styling */
  triggerClassName?: string;
  /** Position of the dropdown. Defaults to 'start' */
  dropdownAlign?: "start" | "end";
  /** When true, sorts options alphabetically by label (case-insensitive) */
  sortAlphabetically?: boolean;
  /** When true in multi-select, selected values appear on top (at open) */
  sortSelectedToTop?: boolean;
  /** Show tooltip on hover. Defaults to false */
  showTooltip?: boolean;
  /** Optional custom trigger node. When provided, used as child of PopoverTrigger. */
  customTrigger?: React.ReactNode;
  /** Enable multi-select mode */
  multiSelect?: boolean;
  /** Selected values when in multi-select mode (controlled) */
  selectedValues?: T[];
  /** Change handler for multi-select mode */
  onChangeMultiple?: (values: T[]) => void;
  /** When true, shows a Save button and only fires onChangeMultiple on save */
  multiSelectShowSaveButton?: boolean;
  /** Optional label for the Save button (defaults to "Save") */
  multiSelectSaveLabel?: string;
  multiSelectRequired?: boolean;
  /** Optional className for the Save button */
  multiSelectButtonClassName?: string;
  multiSelectButtonIcon?: React.ReactNode;
  /** When true, commits changes immediately on selection. When false (default), only commits on Apply button click */
  multiSelectCommitOnChange?: boolean;
  /** Enable select all checkbox in multi-select mode. Shows a checkbox before the search input that toggles all filtered options */
  enableMultiSelectAll?: boolean;
  contentClassName?: string;
  sideOffset?: number;
  alignOffset?: number;
  collisionPadding?: number;
  /** When true, renders only the dropdown content (search + options + save button) without Popover/trigger wrapper. Useful for embedding inside other panels. */
  renderContentOnly?: boolean;
};

/**
 * A customizable dropdown component with support for labels, icons, and flexible positioning.
 *
 * @example
 * ```tsx
 * import { LuCalendar } from "react-icons/lu";
 *
 * // Basic usage with label on the left (default)
 * <SelectDropdown
 *   options={[
 *     { key: "7d", label: "Last 7 Days", value: "7d" },
 *     { key: "14d", label: "Last 14 Days", value: "14d" },
 *     { key: "28d", label: "Last 28 Days", value: "28d" }
 *   ]}
 *   selected="7d"
 *   onChange={(value) => console.log(value)}
 *   label="Date Range"
 *   labelIcon={LuCalendar}
 * />
 *
 * // With label on the right side
 * <SelectDropdown
 *   options={options}
 *   selected={selected}
 *   onChange={onChange}
 *   label="Status"
 *   labelPosition="right"
 *   labelIcon={LuCheckCircle}
 * />
 * ```
 */
function SelectDropdown<T>({
  options,
  selected,
  defaultSelected,
  onSetDefault,
  onChange,
  className,
  disabled,
  label,
  labelIcon,
  labelPosition = "left",
  placeholder = "Select",
  enableSearch = false,
  searchPlaceholder = "Search...",
  showOutline = true,
  triggerClassName,
  dropdownAlign = "start",
  sortAlphabetically = false,
  sortSelectedToTop = false,
  showTooltip = false,
  customTrigger,
  multiSelect = false,
  selectedValues,
  onChangeMultiple,
  multiSelectShowSaveButton = false,
  multiSelectRequired,
  multiSelectButtonClassName,
  multiSelectButtonIcon,
  multiSelectSaveLabel = "Save",
  multiSelectCommitOnChange = false,
  enableMultiSelectAll = false,
  contentClassName = "",
  sideOffset = 7,
  alignOffset = 0,
  collisionPadding = 10,
  renderContentOnly = false,
}: SelectDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isScrollable, setIsScrollable] = useState(false);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find((item) => item.value === selected);
  const [internalSelectedValues, setInternalSelectedValues] = useState<T[]>([]);
  const currentSelectedValues: T[] =
    selectedValues !== undefined ? selectedValues : internalSelectedValues;
  const [draftSelectedValues, setDraftSelectedValues] = useState<T[]>([]);
  const displayedSelectedValues: T[] =
    multiSelect && multiSelectShowSaveButton && !multiSelectCommitOnChange
      ? draftSelectedValues
      : currentSelectedValues;
  const selectedValuesAtOpenRef = useRef<T[]>([]);
  const shouldMaintainSortedOrderRef = useRef(false);
  const closingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize draft values from current values on mount and when popover opens in save-mode
  useEffect(() => {
    if (multiSelect && multiSelectShowSaveButton && !multiSelectCommitOnChange) {
      setDraftSelectedValues(currentSelectedValues);
    }
  }, [
    multiSelect,
    multiSelectShowSaveButton,
    multiSelectCommitOnChange,
    currentSelectedValues,
  ]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closingTimeoutRef.current) {
        clearTimeout(closingTimeoutRef.current);
      }
    };
  }, []);

  // Optionally sort options alphabetically (without mutating input)
  // Keep defaultSelected option at the top when sorting
  const sortedOptions = useMemo(() => {
    if (!sortAlphabetically) return options;

    const sortedArray = [...options].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );

    // If there's a defaultSelected, move it to the top
    if (defaultSelected) {
      const defaultIndex = sortedArray.findIndex(
        (option) => option.key === defaultSelected,
      );
      if (defaultIndex > 0) {
        const defaultOption = sortedArray.splice(defaultIndex, 1)[0];
        sortedArray.unshift(defaultOption);
      }
    }

    return sortedArray;
  }, [options, sortAlphabetically, defaultSelected]);

  // Filter options based on search query, exact/starts-with matches first
  const filteredOptions = useMemo(() => {
    if (!enableSearch || !searchQuery.trim()) return sortedOptions;

    const filtered = sortedOptions.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );
    return searchSort(filtered, searchQuery, (o) => o.label);
  }, [sortedOptions, searchQuery, enableSearch]);

  const checkScrollable = () => {
    if (optionsContainerRef.current) {
      const { scrollHeight, clientHeight, scrollTop } = optionsContainerRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 20;
      setIsScrollable(scrollHeight > clientHeight && !isAtBottom);
    }
  };

  // Check if content is scrollable
  useEffect(() => {
    if (open) {
      // Use setTimeout to ensure the DOM is updated after popover opens
      setTimeout(checkScrollable, 0);
    }
  }, [open, filteredOptions, sortedOptions, enableSearch]);

  const handleChange = (value: T) => {
    if (multiSelect) {
      const working = multiSelectShowSaveButton
        ? displayedSelectedValues
        : currentSelectedValues;
      const exists = working.some((v) => v === value);
      const nextValues = exists
        ? working.filter((v) => v !== value)
        : [...working, value];

      if (multiSelectShowSaveButton && !multiSelectCommitOnChange) {
        setDraftSelectedValues(nextValues);
      } else {
        if (selectedValues === undefined) {
          setInternalSelectedValues(nextValues);
        }
        onChangeMultiple?.(nextValues);
      }

      // Do not close popover for multi-select
      return;
    }

    // Only call onChange if the value is actually different (single-select)
    if (value !== selected) {
      onChange(value);
    }
    setOpen(false);
    setTimeout(() => {
      setSearchQuery("");
    }, 300);
  };

  const handleSelectAll = (checked: boolean) => {
    if (!multiSelect) return;

    const optionsToToggle = enableSearch ? filteredOptions : sortedOptions;
    const working = multiSelectShowSaveButton
      ? displayedSelectedValues
      : currentSelectedValues;

    let nextValues: T[];
    if (checked) {
      // Select all filtered options
      const filteredValues = optionsToToggle.map((opt) => opt.value);
      // Merge with existing selections, avoiding duplicates
      nextValues = Array.from(new Set([...working, ...filteredValues]));
    } else {
      // Deselect all filtered options
      const filteredValueSet = new Set(optionsToToggle.map((opt) => opt.value));
      nextValues = working.filter((v) => !filteredValueSet.has(v));
    }

    if (multiSelectShowSaveButton && !multiSelectCommitOnChange) {
      setDraftSelectedValues(nextValues);
    } else {
      if (selectedValues === undefined) {
        setInternalSelectedValues(nextValues);
      }
      onChangeMultiple?.(nextValues);
    }
  };

  // Check if all filtered options are selected
  const areAllFilteredOptionsSelected = useMemo(() => {
    if (!multiSelect || !enableMultiSelectAll) return false;
    const optionsToCheck = enableSearch ? filteredOptions : sortedOptions;
    if (optionsToCheck.length === 0) return false;
    return optionsToCheck.every((opt) => displayedSelectedValues.includes(opt.value));
  }, [
    multiSelect,
    enableMultiSelectAll,
    filteredOptions,
    sortedOptions,
    enableSearch,
    displayedSelectedValues,
  ]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSearchQuery("");
      if (multiSelect && multiSelectShowSaveButton && !multiSelectCommitOnChange) {
        setDraftSelectedValues(currentSelectedValues);
      }
      // Clear the timeout if it exists
      if (closingTimeoutRef.current) {
        clearTimeout(closingTimeoutRef.current);
      }
      // Maintain sorted order during closing animation (typically 200-300ms)
      // Clear it after animation completes to prevent visual jump
      closingTimeoutRef.current = setTimeout(() => {
        shouldMaintainSortedOrderRef.current = false;
        selectedValuesAtOpenRef.current = [];
        closingTimeoutRef.current = null;
      }, 300);
    } else {
      // Clear any pending closing timeout
      if (closingTimeoutRef.current) {
        clearTimeout(closingTimeoutRef.current);
        closingTimeoutRef.current = null;
      }
      // Snapshot selected values at the moment of opening
      selectedValuesAtOpenRef.current = [...currentSelectedValues];
      // Enable sorted order when opening if sortSelectedToTop is enabled
      if (
        multiSelect &&
        sortSelectedToTop &&
        selectedValuesAtOpenRef.current.length > 0
      ) {
        shouldMaintainSortedOrderRef.current = true;
      } else {
        shouldMaintainSortedOrderRef.current = false;
      }
    }
  };

  const renderIcon = () => {
    if (!labelIcon) return null;
    const LabelIconComponent = labelIcon;
    return (
      <LabelIconComponent className="h-[14px] w-[14px] text-brand-primary-400 shrink-0" />
    );
  };

  const renderLabel = () => {
    if (!label) return null;

    return (
      <div className="flex items-center gap-2 shrink-0 cursor-pointer">
        <span className="overflow-hidden text-brand-primary-600 text-ellipsis text-[11px] font-normal leading-none tracking-[0.24px] cursor-pointer">
          {label}
        </span>
      </div>
    );
  };
  const renderSelectedOption = () => {
    const selectedOptions = multiSelect
      ? options.filter((opt) => displayedSelectedValues.includes(opt.value))
      : selectedOption
        ? [selectedOption]
        : [];

    const summaryLabel = (() => {
      if (selectedOptions.length === 0) return placeholder;
      if (!multiSelect) return selectedOptions[0]?.label;
      if (selectedOptions.length <= 2) {
        return selectedOptions.map((o) => o.label).join(", ");
      }
      return `Multiple selected`;
    })();

    const fullLabel = multiSelect
      ? selectedOptions.map((o) => o.label).join(", ") || placeholder
      : selectedOption?.label || placeholder;

    const renderContent = (
      <span
        className={cn(
          "cursor-pointer flex-1 line-clamp-1 truncate text-brand-primary-new/80 text-[11px] leading-none tracking-[0.24px] capitalize",
          showOutline ? "font-medium" : "font-normal",
          selectedOptions?.length <= 0 ? "text-brand-primary-new/40 font-normal" : "",
        )}
      >
        {summaryLabel}
      </span>
    );

    if (showTooltip) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{renderContent}</TooltipTrigger>
            <TooltipContent className="max-w-[450px] text-wrap">
              {fullLabel.split(",").map((line, idx) => (
                <div key={idx}>{line.trim()}</div>
              ))}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return renderContent;
  };

  const renderTrigger = () => (
    <div className="flex items-center gap-[6px] overflow-hidden">
      {renderIcon()}
      {labelPosition === "left" ? (
        <>
          {renderLabel()}
          {renderSelectedOption()}
        </>
      ) : (
        <>
          {renderSelectedOption()}
          {renderLabel()}
        </>
      )}
    </div>
  );

  const renderOptions = () => {
    const optionsToRender = enableSearch ? filteredOptions : sortedOptions;

    // In multi-select mode, only when open and when enabled, show selected-at-open items on top
    // Use shouldMaintainSortedOrderRef to maintain order during closing animation
    const optionsWithSelectedOnTop = (() => {
      const shouldSort = open || shouldMaintainSortedOrderRef.current;
      if (
        !shouldSort ||
        !multiSelect ||
        !sortSelectedToTop ||
        selectedValuesAtOpenRef.current.length === 0
      )
        return optionsToRender;
      const selectedSet = new Set(selectedValuesAtOpenRef.current);
      const selectedList = optionsToRender.filter((o) => selectedSet.has(o.value));
      const unselectedList = optionsToRender.filter((o) => !selectedSet.has(o.value));
      if (sortAlphabetically) {
        selectedList.sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
        );
        unselectedList.sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
        );
      }
      return [...selectedList, ...unselectedList];
    })();

    return optionsWithSelectedOnTop.length > 0 ? (
      optionsWithSelectedOnTop.map((item) => (
        <div
          key={item.key}
          onClick={() => handleChange(item.value)}
          className={cn(
            "group cursor-pointer w-full px-[8px] py-[4px] rounded-[8px]",
            !multiSelect && selectedOption?.key === item.key
              ? "bg-selected-gray"
              : "hover:bg-hover-gray",
          )}
        >
          <div className="flex justify-between items-center gap-2">
            <div className="w-full flex items-center gap-2">
              {multiSelect && (
                <Checkbox
                  checked={displayedSelectedValues.includes(item.value)}
                  onCheckedChange={() => handleChange(item.value)}
                  className="h-[14px] w-[14px] rounded-[4px]"
                />
              )}
              {item.icon && (
                <item.icon className="h-[14px] w-[14px] text-brand-primary-400 shrink-0" />
              )}
              <span className="text-brand-primary-800 text-[12px] font-normal leading-[20px]">
                {item.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {item.info && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center">
                        <RiInformationLine size={14} className="text-brand-primary-400" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      align="start"
                      className="max-w-[220px] text-[12px]"
                    >
                      {item.info}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {!multiSelect && defaultSelected && item.key === defaultSelected && (
                <div className="h-[20px] flex items-center justify-center px-1 text-[11px] text-brand-orange font-medium squircle-mask squircle-radius-[6px] squircle-smooth-[1] bg-brand-orange/10">
                  <span className="mt-[1px]">Default</span>
                </div>
              )}
              {!multiSelect && onSetDefault && item.key !== defaultSelected && (
                <button
                  className="h-[20px] opacity-0 group-hover:opacity-100 transition-opacity duration-200 group-hover:duration-1000 flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetDefault?.(item.key);
                  }}
                >
                  <span className="mt-[1px] text-[11px] text-brand-primary-600 hover:text-brand-orange whitespace-nowrap">
                    Set as Default
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      ))
    ) : enableSearch && searchQuery.trim() ? (
      <div className="px-[8px] py-[12px] text-center text-brand-primary-400 text-[11px]">
        No results found
      </div>
    ) : null;
  };

  const getTriggerClassName = () => {
    if (triggerClassName) {
      return cn(triggerClassName, className);
    }

    const baseClasses =
      "flex items-center justify-between overflow-hidden whitespace-nowrap bg-white hover-gray text-[11px] focus:outline-none focus:ring-0 cursor-pointer";
    const outlineClasses = showOutline
      ? "h-[32px] px-3 py-[6px] rounded-[8px]  squircle squircle-radius-[8px] squircle-outline-color-border-gray squircle-outline-[1px] shadow-sm"
      : "p-0 shadow-none bg-transparent hover:bg-transparent";

    return cn(
      baseClasses,
      outlineClasses,
      className,
      internalSelectedValues.length <= 0 ? "text-brand-primary-new/40" : "",
    );
  };

  const renderContent = () => (
    <>
      {enableSearch && (
        <div className="mb-[4px]">
          <div className={cn("flex items-center gap-2", multiSelect && enableMultiSelectAll && "pl-[8px]")}>
            {multiSelect && enableMultiSelectAll && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger className="flex items-center">
                    <Checkbox
                      checked={areAllFilteredOptionsSelected}
                      onCheckedChange={handleSelectAll}
                      className="h-[14px] w-[14px] rounded-[4px] shrink-0"
                    />
                    <TooltipContent side="bottom" align="start">
                      Select All
                    </TooltipContent>
                  </TooltipTrigger>
                </Tooltip>
              </TooltipProvider>
            )}
            <div className="relative flex-1">
              <LuSearch
                size={14}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 text-brand-primary-400"
              />
              <Input
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-7 h-[28px] text-[12px] text-brand-primary-new placeholder:text-brand-primary-400 rounded-[8px] bg-selected-gray flex items-center self-stretch pr-[8px] pt-[8px] pb-[6px] border-none focus:ring-0 shadow-none"
              />
            </div>
          </div>
        </div>
      )}
      <div className="relative">
        <div
          ref={optionsContainerRef}
          onScroll={checkScrollable}
          className="max-h-[284px] overflow-y-auto"
        >
          {renderOptions()}
        </div>
        {isScrollable && (
          <div
            className="absolute bottom-0 left-0 right-0 h-[59px] pointer-events-none"
            style={{
              background:
                "linear-gradient(0deg, #FFF 0%, rgba(255, 255, 255, 0.00) 100%)",
            }}
          />
        )}
      </div>
      {multiSelect && multiSelectShowSaveButton && (
        <div className="flex justify-end mt-1">
          <SqButton
            size={"sm"}
            variant={"default"}
            disabled={
              multiSelectRequired
                ? draftSelectedValues.length === 0
                : multiSelectCommitOnChange
                  ? false
                  : JSON.stringify(selectedValues) ===
                    JSON.stringify(draftSelectedValues)
            }
            onClick={() => {
              if (multiSelectCommitOnChange) {
                setOpen(false);
                setTimeout(() => {
                  setSearchQuery("");
                }, 300);
              } else {
                const valuesToSave = draftSelectedValues;
                if (selectedValues === undefined) {
                  setInternalSelectedValues(valuesToSave);
                }
                onChangeMultiple?.(valuesToSave);
                setOpen(false);
                setTimeout(() => {
                  setSearchQuery("");
                }, 300);
              }
            }}
            className={cn("squircle-mask", multiSelectButtonClassName)}
          >
            {multiSelectButtonIcon && multiSelectButtonIcon}
            {multiSelectSaveLabel}
          </SqButton>
        </div>
      )}
    </>
  );

  if (renderContentOnly) {
    return (
      <div
        className={cn(
          "max-w-[400px] p-1 squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white w-auto drop-shadow-md",
          enableSearch ? "min-w-[185px]" : "min-w-[133px]",
          contentClassName,
        )}
      >
        {renderContent()}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={true}>
      <PopoverTrigger
        disabled={disabled}
        className={customTrigger ? undefined : getTriggerClassName()}
        asChild={!!customTrigger}
      >
        {customTrigger ? (
          customTrigger
        ) : (
          <div className={`flex items-center overflow-hidden justify-between w-full`}>
            {renderTrigger()}
            {!disabled && (
              <RiArrowDownSLine className="ml-[6px] shrink-0" size={14} color="#9aa3bd" />
            )}
          </div>
        )}
      </PopoverTrigger>
      <PopoverContent
        align={dropdownAlign}
        className={cn(
          "max-w-[400px] p-1 shadow-none border-0 squircle squircle-smooth-[1] squircle-radius-[12px] rounded-[12px] squircle-outline-[1px] squircle-outline-color-border-gray bg-white w-auto drop-shadow-md",
          enableSearch ? "min-w-[185px]" : "min-w-[133px]",
          contentClassName,
        )}
        portalled={true}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        collisionPadding={collisionPadding}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {renderContent()}
      </PopoverContent>
    </Popover>
  );
}

export default SelectDropdown;
